import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, Copy, Check, ExternalLink, Settings } from 'lucide-react';
import { getWhatsAppStatus } from '../../utils/api';
import { useGlobalFontSize } from '../../context/GlobalFontSizeContext';

const BIQ_PHONE_NUMBER = '+44 7404 140196';
const BIQ_PHONE_NUMBER_RAW = '447404140196';

const WhatsAppBiQSection = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  // Internationalized pre-written message
  const preWrittenMessage = i18n.language === 'es'
    ? 'Hola,%20%C2%BFc%C3%B3mo%20puedes%20ayudarme?'
    : 'Hi,%20how%20can%20you%20help%20me?';
  const whatsappLink = `https://wa.me/${BIQ_PHONE_NUMBER_RAW}?text=${preWrittenMessage}`;
  const { fontSize } = useGlobalFontSize();
  const [copied, setCopied] = useState(false);

  const handleGoToAccount = () => {
    navigate('/account');
  };

  const { data: whatsappStatus, isLoading, isError } = useQuery({
    queryKey: ['whatsappStatus'],
    queryFn: getWhatsAppStatus,
    staleTime: 5 * 60 * 1000, // 5 minutes cache
    retry: 1,
  });

  // Treat error state like not-linked state (graceful degradation)
  const isLinked = !isError && whatsappStatus?.linked === true;

  const handleCopyNumber = async () => {
    try {
      await navigator.clipboard.writeText(BIQ_PHONE_NUMBER);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleOpenWhatsApp = () => {
    window.open(whatsappLink, '_blank');
  };

  if (isLoading) {
    return (
      <div className="border border-[#C3C4C3] dark:border-[#828382] rounded-lg p-[16px] bg-white dark:bg-[#191919]">
        <div className="animate-pulse">
          <div className="h-5 w-32 bg-gray-300 dark:bg-gray-700 rounded mb-2"></div>
          <div className="h-4 w-48 bg-gray-200 dark:bg-gray-800 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-[#C3C4C3] dark:border-[#828382] rounded-lg p-[16px] bg-white dark:bg-[#191919]">
      {/* Header */}
      <div className="mb-4">
        <h3
          className="font-semibold text-bloom-green mb-1"
          style={{ fontSize: fontSize + 2 }}
        >
          {t('whatsapp.title')}
        </h3>
        <p
          className="text-[#5D625F] dark:text-[#828382]"
          style={{ fontSize: fontSize - 2 }}
        >
          {isLinked ? t('whatsapp.linkedSubtitle') : t('whatsapp.notLinkedSubtitle')}
        </p>
      </div>

      {isLinked ? (
        /* Linked State: Show both options */
        <div>
          {/* Open WhatsApp Button */}
          <button
            onClick={handleOpenWhatsApp}
            className="flex items-center gap-2 px-4 py-2.5 bg-[#25D366] hover:bg-[#20BD5A] text-white rounded-lg transition-colors"
            style={{ fontSize: fontSize - 1 }}
          >
            <MessageCircle size={18} />
            {t('whatsapp.openChat')}
            <ExternalLink size={14} />
          </button>

          {/* Or separator */}
          <div className="flex items-center gap-3 my-3">
            <div className="flex-1 h-[1px] bg-[#C3C4C3] dark:bg-[#828382]"></div>
            <span className="text-[#5D625F] dark:text-[#828382] text-sm">{t('common.or')}</span>
            <div className="flex-1 h-[1px] bg-[#C3C4C3] dark:bg-[#828382]"></div>
          </div>

          {/* Fallback for users without WhatsApp desktop */}
          <p
            className="text-[#5D625F] dark:text-[#828382] mb-2"
            style={{ fontSize: fontSize - 2 }}
          >
            {t('whatsapp.noDesktopApp')}
          </p>

          {/* Phone number with copy button */}
          <div className="flex items-center gap-3 p-3 bg-[#F0F5F2] dark:bg-[#232121] border border-[#C3C4C3] dark:border-[#828382] rounded-lg">
            <MessageCircle size={18} className="text-[#25D366] flex-shrink-0" />
            <span
              className="font-medium text-[#282A29] dark:text-[#ffffff] flex-1"
              style={{ fontSize: fontSize }}
            >
              {BIQ_PHONE_NUMBER}
            </span>
            <button
              onClick={handleCopyNumber}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-[#282a29] hover:bg-gray-100 dark:hover:bg-[#3a3c3b] rounded transition-colors"
              style={{ fontSize: fontSize - 2 }}
            >
              {copied ? (
                <>
                  <Check size={14} className="text-[#25D366]" />
                  <span className="text-[#25D366]">{t('whatsapp.copied')}</span>
                </>
              ) : (
                <>
                  <Copy size={14} className="text-[#5D625F] dark:text-[#828382]" />
                  <span className="text-[#5D625F] dark:text-[#828382]">
                    {t('whatsapp.copyNumber')}
                  </span>
                </>
              )}
            </button>
          </div>
        </div>
      ) : (
        /* Not Linked State: Show CTA to add WhatsApp number */
        <div className="p-3 bg-bloom-green/10 border border-bloom-green/30 rounded-lg">
          <p
            className="text-[#282A29] dark:text-[#f0f5f2] mb-3"
            style={{ fontSize: fontSize - 1 }}
          >
            {t('whatsapp.addNumberInstruction')}
          </p>
          <button
            onClick={handleGoToAccount}
            className="flex items-center gap-2 px-4 py-2.5 bg-bloom-green hover:bg-bloom-green-hover text-white rounded-lg transition-colors"
            style={{ fontSize: fontSize - 1 }}
          >
            <Settings size={16} />
            {t('whatsapp.goToAccount')}
          </button>
        </div>
      )}
    </div>
  );
};

export default WhatsAppBiQSection;
